import {Observable} from "rxjs";
import {Observer} from "rxjs";
import {Subscription} from "rxjs";
import {BugsnagLoggingService} from "../logging/bugsnag-logging.service";
import {HttpClient} from "@angular/common/http";
import {ErrorType} from "../../constants";

export class AudioContextWrapper {
    private audioContext: AudioContext;
    private audioBufferSource: AudioBufferSourceNode;
    private endedCallback: Function;
    private playingCallback: Function;
    private pauseCallback: Function;
    private errorCallback: Function;
    private subscriptions: Subscription[] = [];
    private pauseCompleteObserver: Observer<boolean>;

    // This class implements the needed (used in AudioPlaybackService) parts of the HTMLMediaElement interface.
    // currentTime and readyState are set to values such that isAudioPlayerPlaying will do what we want (we only
    // mimic 'paused' and 'ended' behaviors currently)
    public currentTime = 0;
    public ended = true;
    public paused = true;
    public playEventStarted = false;
    public src: string;

    constructor(private http: HttpClient,
                private log: BugsnagLoggingService) {
        // tslint:disable-next-line:no-string-literal
        window.AudioContext = window.AudioContext || window["webkitAudioContext"];
        this.audioContext = new AudioContext();
        this.log.info("Audio: audio context state initial: " + this.audioContext.state);
        document.body.addEventListener("touchend", () =>
            this.audioContext.resume().then(() => {
                this.log.info("Audio: audio context state after resume: " + this.audioContext.state);
            }));

    }

    public pause(): Observable<boolean> {
        // add this.pauseCompleteObserver here, do next/complete in callback to audioBufferSource
        return Observable.create((observer: Observer<boolean>) => {
            if (this.pauseCompleteObserver) {
                this.log.debug("Audio: pauseCompleteObserver already defined, overwriting!");
            }
            this.pauseCompleteObserver = observer;
            this.subscriptions.forEach((sub) => {
                this.log.debug("Audio: unsubscribing outstanding http request");
                sub.unsubscribe();
                this.playEventStarted = false;
            });
            this.subscriptions = [];
            if (this.audioBufferSource && this.isPlaying()) {
                try {
                    this.audioBufferSource.stop();
                } catch (e) {
                    this.log.error("Audio: error calling AudioBufferSourceNode.stop");
                    this.reset();
                    this.resolvePause();
                }
                // set this flag here so that when we get the ended event, we know it was due to a pause/cancellation of
                // the audio (in this case the ended callback should not be called)
                this.paused = true;
            } else {
                this.resolvePause();
            }
        });
    }

    public play(): void {
        // HTMLMediaElement play seems to imply a pause of currently playing audio, so doing that here.
        this.pause().subscribe();
        this.playAudio();
    }

    public isPlaying(): boolean {
        return this.currentTime > 0 && !this.paused && !this.ended;
    }

    private resolvePause(): void {
        if (this.pauseCompleteObserver) {
            this.pauseCompleteObserver.next(true);
            if (this && this.pauseCompleteObserver) {
                this.pauseCompleteObserver.complete();
            } else {
                this.log.info("Audio: pauseCompleteObserver is no longer defined, cannot call complete()");
            }
            this.pauseCompleteObserver = null;
        }
    }

    private playAudio(): void {

        let getAudio = this.http.get(this.src, {responseType: "arraybuffer"})
        //Checking if accessing slc, which requires credentials. Otherwise
        //using content server, which does not.
        if (this.src.indexOf("slc/services/ra") >= 0) {
            getAudio = this.http.get(this.src, {withCredentials: true, responseType: "arraybuffer"});
        }

        let sub = getAudio.subscribe(
            (data) => {
                let audioData = data;
                this.log.info("Audio: Got audio file data of length: " + audioData.byteLength);
                this.playEventStarted = false;
                this.audioContext.decodeAudioData(audioData, (buffer) => {
                                                      this.log.debug("Audio: playing decoded audio for src" + this.src);
                                                      this.playBuffer(buffer);
                                                      if (this.playingCallback) {
                                                          this.playingCallback();
                                                      }
                                                      this.currentTime = 1;
                                                      this.subscriptions = this.subscriptions.filter((s) => s !== sub);
                                                  },
                                                  () => {
                                                      this.log.error("Audio: Error with decoding audio data. file:"
                                                                     + this.src, ErrorType.PLAYBACK);
                                                      this.subscriptions = this.subscriptions.filter((s) => s !== sub);
                                                  });

            }, (error: Response) => {
                this.log.error("Audio: Could not retrieve audio file: " + this.src + ". Error: " +
                               error.statusText + ":" + error.status.toString(), ErrorType.PLAYBACK);
                this.reset();
                if (this.errorCallback) {
                    this.log.debug("Audio: calling error callback");
                    this.errorCallback(this.src + ":" + error.statusText + ":" + error.status.toString());
                }
            });
        this.subscriptions.unshift(sub);
    }

    private playBuffer(buffer: AudioBuffer):void {
        this.audioBufferSource = this.audioContext.createBufferSource();
        this.audioBufferSource.buffer = buffer;
        this.audioBufferSource.addEventListener("ended", () => {
            this.log.debug("Audio: got ended event paused=" + this.paused + ", have ended callback: "
                         + !!this.endedCallback + " have pause callback: " + !!this.pauseCallback);
            let eventDueToPause = this.paused;
            this.reset();
            this.resolvePause();
            if (eventDueToPause) {
                if (this.pauseCallback) {
                    this.pauseCallback();
                }
            } else {
                this.audioBufferSource.stop();
                if (this.endedCallback) {
                    this.endedCallback();
                }
            }
        });
        this.audioBufferSource.connect(this.audioContext.destination);
        this.audioBufferSource.start(0);
        this.paused = false;
        this.ended = false;
    }

    private reset(): void {
        this.ended = true;
        this.currentTime = 0;
        this.playEventStarted = false;
        this.paused = true;
    }

    public addEventListener(type: string, listener: EventListenerOrEventListenerObject, useCapture?: boolean): void {
        switch (type) {
            case "ended":
                this.log.debug("Audio: adding event listener ended callback:" + !!listener);
                this.endedCallback = <Function>listener;
                break;
            case "play":
                this.playingCallback = <Function>listener;
                break;
            case "pause":
                this.log.debug("Audio: adding event listener pause callback:" + !!listener);
                this.pauseCallback = <Function>listener;
                break;
            case "error":
                this.log.debug("Audio: adding event listener error callback:" + !!listener);
                this.errorCallback = <Function>listener;
                break;
            default:
                break;
        }
    }

    public removeEventListener(type: string,
                               listener?: EventListenerOrEventListenerObject, useCapture?: boolean): void {
        switch (type) {
            case "ended":
                this.endedCallback = null;
                break;
            case "play":
                this.playingCallback = null;
                break;
            case "pause":
                this.pauseCallback = null;
                break;
            case "error":
                this.errorCallback = null;
                break;
            default:
                break;
        }
    }
}
